import React from 'react';
import { createContext } from 'use-context-selector';
import { useLazyQuery, useMutation } from '@apollo/client';

import { IPaginatedResponseBase } from '@schemas/general/types';
import {
  IGetLeadProposalsIndexerListParams,
  ILeadProposalsIndexer,
  LeadProposalsIndexerContextData,
  UpdateFavoriteSimulationParms,
} from './types';

import { GET_LEAD_SIMULATIONS_QUERY } from '@hooks/lead-simulations/queries/get-lead-simulations/query';
import { GET_LEADS_WITH_SIMULATIONS_QUERY } from '@hooks/lead-simulations/queries/get-leads-with-simulations/query';
import { DELETE_LEAD_SIMULATIONS_MUTATION } from '@hooks/lead-simulations/mutations/delete-lead-simulations/mutation';
import { SET_FAVORITE_SIMULATION_MUTATION } from '@hooks/lead-simulations/mutations/set-favorite-simulation/mutation';

export const LeadProposalsIndexerContext = createContext({} as LeadProposalsIndexerContextData);

interface Provider {
  children: React.ReactNode;
}

const LeadProposalsIndexerProvider: React.FC<Provider> = ({ children }) => {
  const [leadProposalsIndexer, setLeadProposalsIndexer] = React.useState<ILeadProposalsIndexer>();
  const [leadProposalsIndexerList, setLeadProposalsIndexerList] = React.useState<
    IPaginatedResponseBase<ILeadProposalsIndexer> | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [updatedFavoriteSimulation, setUpdatedFavoriteSimulation] = React.useState<string | null>(null);
  const [isLoadingUpdatedFavoriteSimulationWithSucess, setIsLoadingUpdatedFavoriteSimulationWithSucess] =
    React.useState<boolean>(false);

  const [fetchById, { loading: isLoadingLeadProposalsIndexer }] = useLazyQuery(GET_LEAD_SIMULATIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [fetchList, { loading: isLoadingLeadProposalsIndexerList }] = useLazyQuery(GET_LEADS_WITH_SIMULATIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [executeDelete, { loading: isDeletingSimulation }] = useMutation(DELETE_LEAD_SIMULATIONS_MUTATION);
  const [executeFavorite] = useMutation(SET_FAVORITE_SIMULATION_MUTATION);

  const latestLeadProposalsIndexerRequestId = React.useRef(0);

  const getLeadProposalsIndexerById = React.useCallback(
    (leadId: string): Promise<void> => {
      const requestId = ++latestLeadProposalsIndexerRequestId.current;
      return fetchById({ variables: { leadId } }).then(({ data }) => {
        // Discard responses from superseded calls (e.g. StrictMode's double-invoked
        // mount effect, or a refetch right after delete) to avoid an older, in-flight
        // request overwriting the state with stale data once it resolves out of order.
        if (requestId !== latestLeadProposalsIndexerRequestId.current) return;
        if (data?.leadSimulations) {
          setLeadProposalsIndexer(data.leadSimulations as ILeadProposalsIndexer);
        }
      });
    },
    [fetchById],
  );

  const getLeadProposalsIndexerList = React.useCallback(
    (params?: IGetLeadProposalsIndexerListParams): Promise<void> => {
      const sortParams =
        params?.sortParams && Object.keys(params.sortParams).length > 0 ? JSON.stringify(params.sortParams) : undefined;

      return fetchList({
        variables: {
          page: params?.page,
          limit: params?.limit,
          term: params?.term,
          sortParams,
        },
      }).then(({ data }) => {
        if (data?.leadsWithSimulations) {
          setLeadProposalsIndexerList(data.leadsWithSimulations as IPaginatedResponseBase<ILeadProposalsIndexer>);
        }
      });
    },
    [fetchList],
  );

  const updateFavoriteSimulation = React.useCallback(
    (params: UpdateFavoriteSimulationParms): Promise<void> => {
      setIsLoadingUpdatedFavoriteSimulationWithSucess(true);
      return executeFavorite({
        variables: { leadId: params.leadId, simulationId: params.simulationId },
      })
        .then(({ data }) => {
          const success: boolean = data?.setFavoriteSimulationForLead ?? false;
          setUpdatedFavoriteSimulation(success ? params.simulationId : null);
        })
        .finally(() => {
          setIsLoadingUpdatedFavoriteSimulationWithSucess(false);
        });
    },
    [executeFavorite],
  );

  const deleteSimulation = React.useCallback(
    (leadId: string): Promise<void> => {
      return executeDelete({ variables: { leadId } }).then(() => {
        setLeadProposalsIndexerList((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: prev.data.filter((item) => item.leadInfo?.leadId !== leadId),
          };
        });
      });
    },
    [executeDelete],
  );

  return (
    <LeadProposalsIndexerContext.Provider
      value={{
        getLeadProposalsIndexerById,
        getLeadProposalsIndexerList,
        updateFavoriteSimulation,
        updatedFavoriteSimulation,
        isLoadingUpdatedFavoriteSimulationWithSucess,
        deleteSimulation,
        isDeletingSimulation,
        leadProposalsIndexer,
        leadProposalsIndexerList,
        isLoadingLeadProposalsIndexer,
        isLoadingLeadProposalsIndexerList,
        setCurrentPage,
        currentPage,
      }}
    >
      {children}
    </LeadProposalsIndexerContext.Provider>
  );
};

export default LeadProposalsIndexerProvider;
